import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { usePageMeta } from '@/hooks/usePageMeta';


const CANONICAL = 'https://culturelens.org/guides/measuring-organizational-culture';
const TITLE = 'How to measure organizational culture in a mid-market company';
const DESCRIPTION =
  'How mid-market and PE-backed leaders measure culture without an annual survey, and close the gap between stated and lived.';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: TITLE,
  description: DESCRIPTION,
  mainEntityOfPage: CANONICAL,
  author: { '@type': 'Organization', name: 'CultureLens' },
  publisher: { '@type': 'Organization', name: 'CultureLens' },
  inLanguage: 'en',
};

export default function MeasuringOrganizationalCulture() {
  usePageMeta({
    title: `${TITLE} | CultureLens`,
    description: DESCRIPTION,
    canonical: CANONICAL,
    jsonLd: [jsonLd],
  });

  return (
    <div className="min-h-screen bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />


      <main className="pt-28 pb-24">
        <article className="max-w-content mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-charcoal/70 hover:text-ink mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <header className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.18em] text-charcoal/60 mb-4">
              Guide
            </p>
            <h1 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
              How to measure organizational culture in a mid-market company
            </h1>
            <p className="mt-6 text-lg text-charcoal/85 leading-relaxed">
              Leaders feel a problem long before a survey finds it. This guide is for the point
              where the company is too big to read by walking the floor and too small for a full
              engagement function.
            </p>
          </header>

          <div className="prose-content mt-12 space-y-14 max-w-3xl">
            <Section id="what-is-culture" title="What culture actually is">
              <p>
                Culture is how work really gets done when no one is watching. Not the values on
                the wall. Not last year's engagement score.
              </p>
              <p>
                What matters is the distance between what a company says it stands for and what
                people experience. In a mid-market or PE-backed company that distance is where
                the money leaks: slow decisions, weak managers, good people leaving.
              </p>
            </Section>

            <Section id="stated-vs-lived" title="Stated vs lived">
              <p>
                <strong>Stated</strong> is the intended culture. <strong>Lived</strong> is what
                people would say if they were certain no one could trace it back to them.
              </p>
              <p>
                A healthy company is not one with no gap. It is one where the gap is visible,
                small enough to close, and moving the right way.
              </p>
            </Section>

            <Section id="why-surveys-fall-short" title="Why annual surveys fail">
              <ol>
                <li>
                  <strong>Averages hide the problem.</strong> A score of 72 will not tell you
                  trust is collapsing in one function.
                </li>
                <li>
                  <strong>Scales flatten the answer.</strong> People describe problems in
                  sentences, not numbers.
                </li>
                <li>
                  <strong>Honesty runs out.</strong> By year three people know what to write.
                </li>
              </ol>
              <p>
                And the result lands a quarter after the damage is done.
              </p>
            </Section>

            <Section id="framework" title="A practical way to measure it">
              <h3 className="font-display text-2xl mt-8">1. Pick six to eight anchors</h3>
              <p>
                Trust in leadership, clarity of direction, safety to speak up, quality of
                feedback, fairness of recognition. Fewer and you miss the shape. More and it
                blurs.
              </p>
              <h3 className="font-display text-2xl mt-8">2. Ask daily, not annually</h3>
              <p>
                One to three short questions a day beat an annual survey on response rate,
                honesty, and speed to act.
              </p>
              <h3 className="font-display text-2xl mt-8">3. Capture what people say, not just scores</h3>
              <p>
                Numbers tell you something changed. Words tell you what and why. Confidential
                conversations give leaders evidence they can act on without exposing anyone.
              </p>
              <h3 className="font-display text-2xl mt-8">4. Build anonymity into the data</h3>
              <p>
                Enforce a minimum group size (k of five or more), keep raw answers out of leader
                views, and say plainly what leaders can and cannot see. Anonymity as a promise
                is fragile. Anonymity in the data model holds.
              </p>
            </Section>

            <Section id="metrics" title="What to watch">
              <ul>
                <li>
                  <strong>Direction.</strong> An anchor sliding for six weeks matters more than
                  any single score.
                </li>
                <li>
                  <strong>Biggest movers.</strong> The two or three anchors changing fastest are
                  where attention pays.
                </li>
                <li>
                  <strong>Cuts by team, tenure, location.</strong> Tells you whether it is a
                  company problem or a team problem.
                </li>
                <li>
                  <strong>Stated vs lived gap.</strong> Sustained divergence predicts the
                  departures you will regret.
                </li>
              </ul>
            </Section>

            <Section id="how-culturelens-helps" title="Where CultureLens fits">
              <p>
                People answer short questions and, when they want to, talk privately about what
                is really going on. Leaders see anchors, what people are raising, and the gap.
                Never individual answers. Never a group under five.
              </p>
              <p>
                Built for SME and mid-market leaders, PE-backed portfolios, CHROs who need
                evidence, and integration teams.
              </p>
            </Section>

            <div className="mt-16 pt-10 border-t border-hairline">
              <h2 className="font-display text-2xl">See it on live data</h2>
              <p className="mt-3 text-charcoal/85">
                We will show you the anonymity model, the anchors, and the leader view.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <GetStartedDialog>
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full px-6 h-11">
                    Request access
                  </Button>
                </GetStartedDialog>
                <Button asChild variant="ghost" className="rounded-full">
                  <Link to="/who-we-are#contact" className="inline-flex items-center gap-2">
                    Talk to us <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28">
      <h2 className="font-display text-3xl md:text-[32px] tracking-tight">{title}</h2>
      <div className="mt-4 space-y-4 text-[17px] leading-[1.7] text-charcoal/90 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul>li]:mb-2 [&_ol>li]:mb-2">
        {children}
      </div>
    </section>
  );
}
